/**
 * Simple utility to parse scene breakdown from generated scripts
 */

export interface SceneBreakdownData {
  characters: CharacterData[];
  imageScenes: SceneData[];
  musicCues: string[];
  soundEffects: string[];
  estimatedDuration: string;
}

export interface CharacterData {
  characterId: string;        // "CHAR 1"
  characterName: string;      // "Archangel Michael"
  description: string;        // Full character description
  imageUrl?: string;          // Generated character image URL
  isGenerated: boolean;       // Has character image been created
}

export interface SceneData {
  description: string;
  characterTags: string[];    // ["CHAR 1", "CHAR 2"] or []
  hasCharacters: boolean;
}

export interface PromptEstimates {
  image: number;
  music: number;
  sfx: number;
  narration: number;
  characters: number;         // NEW: Character prompt count
}

export class ScriptAnalyzer {
  /**
   * Parse scene breakdown from script content
   */
  static parseSceneBreakdown(scriptContent: string): SceneBreakdownData | null {
    if (!this.hasSceneBreakdown(scriptContent)) {
      return null;
    }

    const characters = this.extractCharacterSection(scriptContent);
    const imageScenes = this.extractImageScenesWithCharacters(scriptContent);

    return {
      characters,
      imageScenes,
      musicCues: this.extractSection(scriptContent, '## MUSIC CUES:'),
      soundEffects: this.extractSection(scriptContent, '## SOUND EFFECTS:'),
      estimatedDuration: this.extractDuration(scriptContent)
    };
  }

  /**
   * Get prompt estimates from script (with or without breakdown)
   */
  static getPromptEstimates(scriptContent: string): PromptEstimates {
    const breakdown = this.parseSceneBreakdown(scriptContent);
    
    if (breakdown) {
      // console.log('ScriptAnalyzer - Found breakdown:', {
      //   imageScenes: breakdown.imageScenes.length,
      //   characters: breakdown.characters.length,
      //   musicCues: breakdown.musicCues.length,
      //   soundEffects: breakdown.soundEffects.length,
      //   imageScenesList: breakdown.imageScenes
      // });
      
      const estimates = {
        image: Math.max(breakdown.imageScenes.length, 1),
        music: Math.max(breakdown.musicCues.length, 1),
        sfx: Math.max(breakdown.soundEffects.length, 1),
        narration: 1,
        characters: breakdown.characters.length
      };
      
      //console.log('ScriptAnalyzer - Returning estimates:', estimates);
      
      return estimates;
    }

    // Fallback to parsing script tags
    //console.log('ScriptAnalyzer - Using legacy parsing');
    return this.legacyEstimates(scriptContent);
  }

  /**
   * Check if script has scene breakdown
   */
  private static hasSceneBreakdown(scriptContent: string): boolean {
    return scriptContent.includes('## IMAGE SCENES:') &&
           scriptContent.includes('## MUSIC CUES:') &&
           scriptContent.includes('## SOUND EFFECTS:');
  }

  /**
   * Extract character section from script
   */
  private static extractCharacterSection(content: string): CharacterData[] {
    // More robust regex that captures everything between ## CHARACTERS: and the next ## section
    const regex = /## CHARACTERS:\s*\n([\s\S]*?)(?=\n## [A-Z]|\n##[A-Z]|$)/i;
    const match = content.match(regex);
    
    if (!match || !match[1]) {
      //console.log('ScriptAnalyzer - No character section found');
      return [];
    }

    const characterSection = match[1].trim();
    //console.log('ScriptAnalyzer - Raw character section:', characterSection);
    
    // Extract individual characters using ### CHARACTER pattern - more flexible regex
    const characterMatches = characterSection.match(/### CHARACTER \d+:[\s\S]*?(?=### CHARACTER \d+:|$)/gi);
    
    if (!characterMatches) {
      //console.log('ScriptAnalyzer - No character matches found');
      return [];
    }

    //console.log('ScriptAnalyzer - Found character matches:', characterMatches.length);

    const characters: CharacterData[] = characterMatches.map((charMatch, index) => {
      const lines = charMatch.split('\n');
      const titleLine = lines[0];
      const characterName = titleLine.replace(/### CHARACTER \d+:\s*/, '').trim();
      const description = lines.slice(1).join('\n').trim();
      
      //console.log(`ScriptAnalyzer - Processing character ${index + 1}:`, characterName);
      
      return {
        characterId: `CHAR ${index + 1}`,
        characterName,
        description,
        imageUrl: undefined,
        isGenerated: false
      };
    });

    //console.log('ScriptAnalyzer - Extracted characters:', characters);
    return characters;
  }

  /**
   * Extract image scenes with character tag parsing
   */
  private static extractImageScenesWithCharacters(content: string): SceneData[] {
    const rawScenes = this.extractSection(content, '## IMAGE SCENES:');
    
    return rawScenes.map(scene => {
      const characterTags = this.parseCharacterTags(scene);
      return {
        description: scene,
        characterTags,
        hasCharacters: characterTags.length > 0
      };
    });
  }

  /**
   * Parse character tags from scene description
   */
  private static parseCharacterTags(sceneText: string): string[] {
    // Handle both formats: "2. [CHAR 1] Scene" and "[CHAR 1] Scene"
    //const numberedMatch = sceneText.match(/^\d+\.\s*\[([^\]]+)\]/);
    //const directMatch = sceneText.match(/^\[([^\]]+)\]/);
    const match = sceneText.match(/\[([^\]]+)\]/);
    
    //const match = numberedMatch || directMatch;
    if (!match) return [];
    
    const tagContent = match[1];
    
    //console.log('🏷️ Parsing character tags:', { sceneText: sceneText.substring(0, 50), tagContent, match: match[0] });
    
    // Handle [NO CHARS] case
    if (tagContent === 'NO CHARS') return [];
    
    // Extract character IDs like CHAR 1, CHAR 2
    const tags = tagContent
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.startsWith('CHAR'));
      
    //console.log('🎭 Extracted character tags:', tags);
    return tags;
  }

  /**
   * Extract numbered list from section
   */
  private static extractSection(content: string, sectionHeader: string): string[] {
    //const regex = new RegExp(`${sectionHeader}\\s*\\n([\\s\\S]*?)(?=\\n##|$)`, 'i');
    const regex = new RegExp(`${sectionHeader}\\s*\\n([\\s\\S]*?)(?=\\n##[^#]|$)`, 'i');
    const match = content.match(regex);
    
    if (!match || !match[1]) {
      //console.log(`ScriptAnalyzer - No match found for section: ${sectionHeader}`);
      return [];
    }

    const sectionContent = match[1].trim();
    //console.log(`ScriptAnalyzer - Raw section content for ${sectionHeader}:`, sectionContent);
    
    const lines = sectionContent.split('\n');
    const items: string[] = [];
    let currentItem = '';
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (!trimmedLine) {
        continue;
      }
      
      // Stop if we hit another section header (## SOMETHING:)
      if (trimmedLine.match(/^##\s+[A-Z\s]+:/)) {
        break;
      }
      
      // Check if this is a numbered line (start of new item)
      if (trimmedLine.match(/\d/)) {
        // Save previous item if it exists
        if (currentItem) {
          items.push(currentItem.trim());
        }
        // Start new item with the numbered line (remove number and period)
        currentItem = trimmedLine.replace(/^\d+\.\s*/, '');
      } 
      // Check if this is a bullet point (part of current item)
      else if (trimmedLine.match(/^[-•]\s*/)) {
        // Only add bullet points if we're currently building an item
        if (currentItem) {
          const bulletContent = trimmedLine.replace(/^[-•]\s*/, '');
          currentItem += ` - ${bulletContent}`;
        }
      }
      // If we encounter any other content and we're not building an item, skip it
    }
    
    // Don't forget the last item
    if (currentItem) {
      items.push(currentItem.trim());
    }
        
    return items;
  }

  /**
   * Extract duration
   */
  private static extractDuration(content: string): string {
    const regex = /## ESTIMATED DURATION:\s*\n([^\n]+)/i;
    const match = content.match(regex);
    return match && match[1] ? match[1].trim() : '2-3 minutes';
  }

  /**
   * Legacy estimates for scripts without breakdown
   */
  private static legacyEstimates(scriptContent: string): PromptEstimates {
    const scenes = scriptContent.match(/\[SCENE[^\]]*\]/gi) || [];
    const music = scriptContent.match(/\[MUSIC[^\]]*\]/gi) || [];
    const sfx = scriptContent.match(/\[SFX[^\]]*\]/gi) || [];
    
    return {
      image: Math.max(scenes.length, 1),
      music: Math.max(music.length, 1),
      sfx: Math.max(sfx.length, 1),
      narration: 1,
      characters: 0  // Legacy scripts don't have characters
    };
  }
}
